import React, { createContext, useState, useContext, useEffect, useRef } from 'react'
import api from '../services/api'
import { Usuario } from '@/types'

type AuthContextType = {
  user: Usuario | null
  loading: boolean
  login: (username: string, password: string) => Promise<any>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  hasPermission: (resource: string, action?: string) => boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<Usuario | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionExpired, setSessionExpired] = useState(false)
  const currentUser = useRef<Usuario | null>(null)
  const loginButton = useRef<HTMLButtonElement>(null)

  const updateUser = (nextUser: Usuario | null) => {
    currentUser.current = nextUser
    setUser(nextUser)
  }

  const expireSession = () => {
    if (!currentUser.current) return
    updateUser(null)
    setSessionExpired(true)
  }

  useEffect(() => {
    const requestUsers = new WeakMap<object, Usuario | null>()
    const requestInterceptor = api.interceptors.request.use((config) => {
      requestUsers.set(config, currentUser.current)
      return config
    })
    const interceptor = api.interceptors.response.use(undefined, (error) => {
      const endpoint = error.config?.url?.split('?')[0]
      if (error.response?.status === 401 && endpoint !== '/auth/login' && endpoint !== '/auth/logout' &&
          error.config && requestUsers.get(error.config) === currentUser.current) {
        expireSession()
      }
      return Promise.reject(error)
    })
    checkAuth()
    return () => {
      api.interceptors.request.eject(requestInterceptor)
      api.interceptors.response.eject(interceptor)
    }
  }, [])

  useEffect(() => {
    if (sessionExpired) loginButton.current?.focus()
  }, [sessionExpired])

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/session')
      if ((response as any)?.success) {
        if (!(response as any).data) expireSession()
        updateUser((response as any).data || null)
      }
    } catch (error) {
      updateUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password })
    if ((response as any)?.success) {
      updateUser((response as any).data)
      setSessionExpired(false)
      return response
    }
    throw new Error((response as any).message)
  }

  const logout = async () => {
    updateUser(null)
    setSessionExpired(false)
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      updateUser(null)
    }
  }

  const hasPermission = (resource: string, action = 'read') => {
    if (!user) return false
    const role = user.role
    if (role === 'admin') return true
    const permissionsByRole: Record<string, string[]> = {
      moderator: [
        'users.read',
        'clients.read',
        'clients.write',
        'properties.read',
        'properties.write',
        'contracts.read',
        'contracts.write',
        'payments.read',
        'payments.write',
        'receipts.read',
        'receipts.write',
        'receipts.cancel',
        'receipts.search',
        'reports.read',
      ],
      user: ['clients.read', 'properties.read', 'contracts.read', 'payments.read'],
      mostrador: [
        // READ-ONLY access to core entities (clients, properties, contracts, payments)
        'clients.read',
        'properties.read',
        'contracts.read',
        'payments.read',
        
        // FULL access to receipts (can create/edit/cancel)
        'receipts.read',
        'receipts.write',
        'receipts.cancel',
        'receipts.search',
        
        'reports.read',

        // Read-only settings access (IRPF thresholds for receipt/contract screens)
        'settings.read',

        // DGI electronic invoicing
        'dgi.read',
        'dgi.write',
      ],
    }
    const key = `${resource}.${action}`
    return (permissionsByRole[role] || []).includes(key)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, hasPermission }}>
      {sessionExpired ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-100 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-expired-title"
            aria-describedby="session-expired-message"
            className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onKeyDown={(event) => {
              if (event.key === 'Tab') {
                event.preventDefault()
                loginButton.current?.focus()
              }
            }}
          >
            <h2 id="session-expired-title" className="text-lg font-semibold text-gray-900">
              Sesión expirada
            </h2>
            <p id="session-expired-message" className="mt-3 text-sm text-gray-600">
              Tu sesión ha expirado. Iniciá sesión nuevamente para continuar.
            </p>
            <button
              ref={loginButton}
              type="button"
              onClick={() => setSessionExpired(false)}
              className="mt-6 w-full rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Volver a iniciar sesión
            </button>
          </div>
        </div>
      ) : children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
